/**
 * Ephemeral demo-session storage (#1753).
 *
 * `POST /api/demo-sessions` mints a throwaway account + team and hands
 * back an opaque bearer token — shown exactly once. We keep it in
 * localStorage so the visitor can survive a reload inside the (2 h)
 * demo window; `readDemoSession()` self-expires past `expires_at`, so
 * every reader sees either a live session or nothing.
 *
 * The token is a REAL credential resolved server-side through the
 * normal auth path (it can only ever reach the one ephemeral team), so
 * unlike the dev-shim there is no path-gating dance: if a live session
 * is stored, `<AuthProvider/>` mounts the demo-session provider.
 */

import { setActiveWorkspaceId } from "./activeWorkspace";
import { clearDevAccountId } from "./auth";
import { consumeReturnTo } from "./returnTo";

const STORAGE_KEY = "tarii.demo_session.v1";

/**
 * Set when a demo session ENDS on its own (#2986): the token ran past
 * `expires_at`, or the API refused it, AND this tab used a live demo session
 * (the `LIVE_KEY` marker). It is not set when the code clears the session on
 * purpose (`leaveDemo`, sign-out, a new demo), nor when a stale token from an
 * old visit is found and cleared on load. The auth gate reads
 * it so a demo visitor's deep link goes back to `/demo` and not to a sign-in
 * they cannot use. It lives in sessionStorage: it is about this tab's visit.
 * Another tab, or a later visit, gets the same redirect only through the
 * `HELD_KEY` record, and only for `DEMO_HELD_FRESH_MS` after the demo ends.
 * The notice carries a "Sign in instead" link that clears both.
 */
const ENDED_KEY = "tarii.demo_ended";

/**
 * Set once the ended-demo notice has shown (#2998). It is separate from
 * `ENDED_KEY` so the redirect keeps working after the notice: Back from
 * `/demo` to a team link goes to `/demo` again, without a second notice.
 */
const NOTICE_SEEN_KEY = "tarii.demo_notice_seen";

/**
 * localStorage record that this browser held a demo (#2998). It holds the
 * session's `expires_at`. A signed-out deep link goes to `/demo` while the
 * record is fresh, so a new tab or a stale token still reaches the demo
 * start. A browser with no record, or an old one, goes to sign-in.
 */
const HELD_KEY = "tarii.demo_held";

/** How long after the demo's expiry the record stays fresh. */
export const DEMO_HELD_FRESH_MS = 6 * 60 * 60 * 1000;

function setHeld(expiresAt: string): void {
  try {
    window.localStorage.setItem(HELD_KEY, expiresAt);
  } catch {
    // Storage blocked: a new tab just shows sign-in, as before.
  }
}

/** True while the "this browser held a demo" record is fresh; drops an old one. */
function heldIsFresh(): boolean {
  try {
    const raw = window.localStorage.getItem(HELD_KEY);
    if (!raw) return false;
    const expiry = Date.parse(raw);
    if (Number.isFinite(expiry) && Date.now() < expiry + DEMO_HELD_FRESH_MS) return true;
    window.localStorage.removeItem(HELD_KEY);
  } catch {
    // Storage blocked: treat as no record.
  }
  return false;
}

/** Set while this tab holds a live demo session; cleared with the token. */
const LIVE_KEY = "tarii.demo_live";

function setLive(on: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (on) window.sessionStorage.setItem(LIVE_KEY, "1");
    else window.sessionStorage.removeItem(LIVE_KEY);
  } catch {
    // Storage blocked: an ended demo shows sign-in, as before.
  }
}

function isLive(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(LIVE_KEY) === "1";
  } catch {
    return false;
  }
}

export function markDemoEnded(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(ENDED_KEY, "1");
    window.sessionStorage.removeItem(NOTICE_SEEN_KEY);
  } catch {
    // Storage blocked: the visitor just sees sign-in, as before.
  }
}

/**
 * The redirect signal: this tab saw a demo end, or this browser holds a fresh
 * record of a demo (a new tab, a stale token). The notice does not clear it.
 */
export function isDemoEnded(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(ENDED_KEY) === "1") return true;
  } catch {
    // Fall through to the browser record.
  }
  return heldIsFresh();
}

/** Read-only: does this browser still store a demo session token? */
export function hasStoredDemoSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Pure read: should the ended-demo notice show? True when the demo ended, no
 * session is stored, and this tab has not shown the notice yet.
 */
export function shouldShowDemoEndedNotice(): boolean {
  if (typeof window === "undefined") return false;
  if (!isDemoEnded() || hasStoredDemoSession()) return false;
  try {
    return window.sessionStorage.getItem(NOTICE_SEEN_KEY) !== "1";
  } catch {
    return true;
  }
}

/** Record that this tab showed the notice. Call it from an effect. */
export function markDemoEndedNoticeSeen(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(NOTICE_SEEN_KEY, "1");
  } catch {
    // Storage blocked: the notice may show again; the redirect still works.
  }
}

/** Forget the ended demo: the flags and the browser record. */
export function clearDemoEnded(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(ENDED_KEY);
    window.sessionStorage.removeItem(NOTICE_SEEN_KEY);
  } catch {
    // Nothing to clear.
  }
  try {
    window.localStorage.removeItem(HELD_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** The session ended by itself: clear the token and record that it ended. */
export function endDemoSession(): void {
  const wasLive = isLive();
  clearDemoSession();
  if (wasLive) markDemoEnded();
}

export interface DemoSessionState {
  token: string;
  account_id: string;
  team_id: string;
  cycle_id: string | null;
  team_name: string;
  /** ISO timestamp — the server's TTL; mirrored client-side for self-expiry. */
  expires_at: string;
  solve_time_limit_s: number;
  solves_remaining: number;
  /** The scenario the visitor picked (#2824). The guide reads its considerations. */
  scenario?: string;
  /** The stored change the team shows (#2824), or null for the team as it is. */
  variant?: string | null;
}

export function readDemoSession(): DemoSessionState | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DemoSessionState;
    if (!parsed?.token || !parsed?.expires_at) {
      clearDemoSession();
      return null;
    }
    if (
      Number.isFinite(Date.parse(parsed.expires_at)) &&
      Date.parse(parsed.expires_at) <= Date.now()
    ) {
      // Session lapsed — the backend 401s the token anyway; clearing here
      // keeps the SPA from mounting a dead provider on the next load.
      endDemoSession();
      return null;
    }
    setLive(true);
    setHeld(parsed.expires_at);
    return parsed;
  } catch {
    clearDemoSession();
    return null;
  }
}

// #2824 - the stored session lives in localStorage, which every tab shares. A
// lead's own signed-in tab must not turn into a demo when another tab starts one,
// so "this tab is a demo" follows the mounted provider: the demo providers in
// `AuthContext` set this flag on mount and clear it on unmount.
let demoTab = false;

/** Mark this tab as a demo tab. Called by the demo auth providers only. */
export function setDemoTab(on: boolean): void {
  demoTab = on;
}

/** True when this tab mounted a demo auth provider. */
export function isDemoTab(): boolean {
  return demoTab;
}

/** True inside a demo session in THIS tab: the guided demo (#2824). */
export function isGuidedDemo(): boolean {
  return demoTab && readDemoSession() !== null;
}

export function storeDemoSession(session: DemoSessionState): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  setLive(true);
  clearDemoEnded();
  setHeld(session.expires_at);
}

/** Keep the change the team shows on the stored session. No-op outside a demo. */
export function setDemoVariant(variant: string | null): void {
  const session = readDemoSession();
  if (!session) return;
  storeDemoSession({ ...session, variant });
}

export function clearDemoSession(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
  setLive(false);
}

/**
 * End the demo and hard-load the website (#2779). Every demo exit calls
 * this: the top-bar button, the user menu and the phone profile sheet.
 *
 * It clears storage only and leaves React auth state alone. Calling the
 * provider's `signOut` first dropped the account mid-page, so the auth
 * gate stashed the demo page as the return address and replaced the URL
 * with `/sign-in` before the load. A later sign-up in the same tab then
 * landed on the dead demo team. The stale return address is discarded too,
 * and so is a persona picked on `/demo/scenarios` (the dev-shim id), or `/`
 * would open that persona's team.
 */
export function leaveDemo(to: "/" | "/demo" = "/"): void {
  if (typeof window === "undefined") return;
  clearDemoSession();
  clearDemoEnded();
  // Its listener is the dev-shim provider only. That provider is not mounted
  // in a demo session, and the `/demo` pages are public, so no gate runs.
  clearDevAccountId();
  setActiveWorkspaceId(null);
  consumeReturnTo();
  // "/demo" is the picker: the visitor ends this demo to start another (#2824).
  window.location.assign(to);
}

/**
 * Click handler for a "Back to the website" link (`href="/"`) on the demo
 * entry pages. A plain click ends any demo or persona first, because `/`
 * would otherwise open that team. A modified click (new tab) keeps the
 * browser default.
 */
export function leaveDemoOnClick(event: {
  preventDefault: () => void;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
}): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey) return;
  event.preventDefault();
  leaveDemo();
}

/** The Authorization header value for a demo-session token. */
export function demoSessionAuthValue(token: string): string {
  return `Bearer demo-session:${token}`;
}
